// File: frontend/src/components/admin/common/AdminRefreshButton.tsx

import React from "react";
import { FaSyncAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import "./AdminRefreshButton.css";

interface AdminRefreshButtonProps {
  /** Called when the button is pressed — normally a "reload this panel" fetch. */
  onClick: () => void;
  /**
   * True while a refresh is in flight. Spins the icon and (together with
   * `disabled`) keeps the admin from firing a second request on top of the first.
   */
  refreshing?: boolean;
  /** Extra disable condition, e.g. the initial load of the panel. */
  disabled?: boolean;
  /**
   * "full" (default) renders icon + label, "icon" renders a square icon-only
   * button for tight toolbars. Both share the exact same colours, border and
   * spin animation so every refresh control in the admin panel looks identical.
   */
  variant?: "full" | "icon";
  /**
   * Overrides the button label. Defaults to the shared `admin_refresh` key so
   * all panels say the same thing unless a panel has a reason not to.
   */
  label?: string;
  /** Extra class for one-off placement tweaks. */
  className?: string;
}

/**
 * The single refresh control used by every admin panel.
 *
 * Before this component existed each panel hand-rolled its own button
 * (.users-refresh-btn, .admin-rank-board-refresh-btn, .ct-refresh-btn,
 * .dash-refresh-btn), which drifted apart in padding, radius, colour and
 * spin speed. They are all routed through here now.
 */
export const AdminRefreshButton: React.FC<AdminRefreshButtonProps> = ({
  onClick,
  refreshing = false,
  disabled = false,
  variant = "full",
  label,
  className = "",
}) => {
  const { t } = useTranslation();
  const text = label ?? t("admin_refresh");

  return (
    <button
      type="button"
      className={`admin-refresh-btn admin-refresh-btn--${variant} ${className}`.trim()}
      onClick={onClick}
      disabled={disabled || refreshing}
      title={text}
      aria-label={text}
    >
      <FaSyncAlt className={refreshing ? "admin-refresh-spinning" : ""} />
      {variant === "full" && <span>{text}</span>}
    </button>
  );
};